/**
 * Texture Generator
 * Creates all game sprites programmatically
 */

import { CONSTANTS } from '../config.js';

export class TextureGenerator {
    constructor(scene) {
        this.scene = scene;
    }

    generateAll() {
        this.createQuantatoHappy();
        this.createQuantatoHorror();
        this.createQuantatoPlayer();
        this.createButtons();
        this.createOverlays();
        this.createDataPoints();
        // Skip createOverfitter() - using custom image instead
        this.createParticles();
    }

    createQuantatoHappy() {
        const g = this.scene.make.graphics({ add: false });

        // Body - golden potato shape
        g.fillStyle(CONSTANTS.COLORS.QUANTATO_HAPPY);
        g.fillEllipse(64, 70, 100, 120);

        // Subtle gradient overlay
        g.fillStyle(0xFFE066, 0.3);
        g.fillEllipse(64, 60, 80, 90);

        // Rosy cheeks
        g.fillStyle(CONSTANTS.COLORS.QUANTATO_CHEEK, 0.6);
        g.fillCircle(30, 75, 15);
        g.fillCircle(98, 75, 15);

        // Simple cartoon eyes (placeholders - real eyes added via EyeRenderer)
        g.fillStyle(0x000000);
        g.fillCircle(45, 55, 12);
        g.fillCircle(83, 55, 12);

        // Eye shine
        g.fillStyle(0xFFFFFF);
        g.fillCircle(48, 52, 5);
        g.fillCircle(86, 52, 5);

        // Happy smile
        g.lineStyle(4, 0x000000);
        g.beginPath();
        g.arc(64, 85, 25, 0.2, Math.PI - 0.2, false);
        g.strokePath();

        // Small arms waving
        g.lineStyle(8, CONSTANTS.COLORS.QUANTATO_HAPPY);
        g.beginPath();
        g.moveTo(15, 70);
        g.lineTo(0, 45);
        g.strokePath();
        g.beginPath();
        g.moveTo(113, 70);
        g.lineTo(128, 45);
        g.strokePath();

        g.generateTexture('quantato_happy', 128, 140);
        g.destroy();
    }

    createQuantatoHorror() {
        const g = this.scene.make.graphics({ add: false });

        // Distorted body
        g.fillStyle(CONSTANTS.COLORS.QUANTATO_HORROR);
        g.fillEllipse(64, 70, 105, 125);

        // Glitch overlay effect
        g.fillStyle(0xFF0000, 0.3);
        g.fillEllipse(67, 72, 100, 120);
        g.fillStyle(0x00FF00, 0.15);
        g.fillEllipse(61, 68, 100, 120);

        // Hollow eyes
        g.fillStyle(0x000000);
        g.fillCircle(45, 55, 18);
        g.fillCircle(83, 55, 18);

        // Red pupils
        g.fillStyle(0xFF0000);
        g.fillCircle(45, 55, 6);
        g.fillCircle(83, 55, 6);

        // Twisted mouth
        g.lineStyle(5, 0x8B0000);
        g.beginPath();
        g.moveTo(30, 95);
        g.lineTo(45, 85);
        g.lineTo(64, 100);
        g.lineTo(83, 82);
        g.lineTo(98, 95);
        g.strokePath();

        // Drip effect
        g.fillStyle(0x8B0000, 0.8);
        g.fillRect(40, 100, 4, 20);
        g.fillRect(64, 105, 4, 25);
        g.fillRect(88, 98, 4, 18);

        g.generateTexture('quantato_horror', 128, 140);
        g.destroy();
    }

    createQuantatoPlayer() {
        const g = this.scene.make.graphics({ add: false });

        // Smaller player version with horror tint
        g.fillStyle(CONSTANTS.COLORS.QUANTATO_HORROR);
        g.fillEllipse(32, 35, 50, 60);

        g.fillStyle(0xFF0000, 0.3);
        g.fillEllipse(34, 36, 48, 58);

        g.fillStyle(0x000000);
        g.fillCircle(22, 28, 8);
        g.fillCircle(42, 28, 8);

        g.fillStyle(0xFF0000);
        g.fillCircle(22, 28, 3);
        g.fillCircle(42, 28, 3);

        g.lineStyle(3, 0x8B0000);
        g.beginPath();
        g.arc(32, 48, 12, 0.3, Math.PI - 0.3, true);
        g.strokePath();

        g.generateTexture('quantato_player', 64, 70);
        g.destroy();
    }

    createButtons() {
        const g = this.scene.make.graphics({ add: false });

        // Normal button
        g.fillStyle(CONSTANTS.COLORS.BUTTON_GREEN);
        g.fillRoundedRect(0, 0, 120, 50, 10);
        g.generateTexture('button_normal', 120, 50);
        g.clear();

        // Hover button
        g.fillStyle(CONSTANTS.COLORS.BUTTON_HOVER);
        g.fillRoundedRect(0, 0, 120, 50, 10);
        g.generateTexture('button_hover', 120, 50);
        g.clear();

        // Answer button (math question answers)
        g.fillStyle(0x5C6BC0);
        g.fillRoundedRect(0, 0, 80, 40, 8);
        g.generateTexture('answer_button', 80, 40);

        g.destroy();
    }

    createOverlays() {
        const g = this.scene.make.graphics({ add: false });

        // Scanlines overlay
        g.fillStyle(0x000000, 0.1);
        for (let i = 0; i < 600; i += 3) {
            g.fillRect(0, i, 800, 1);
        }
        g.generateTexture('scanlines', 800, 600);
        g.clear();

        // Glitch overlay
        g.fillStyle(0xFF0000, 0.5);
        for (let i = 0; i < 20; i++) {
            g.fillRect(Math.random() * 800, Math.random() * 600, Math.random() * 200, 3);
        }
        g.fillStyle(0x00FF00, 0.5);
        for (let i = 0; i < 20; i++) {
            g.fillRect(Math.random() * 800, Math.random() * 600, Math.random() * 200, 3);
        }
        g.fillStyle(0x0000FF, 0.5);
        for (let i = 0; i < 20; i++) {
            g.fillRect(Math.random() * 800, Math.random() * 600, Math.random() * 200, 3);
        }
        g.generateTexture('glitch', 800, 600);
        g.clear();

        // Vignette overlay
        const vignetteSize = 400;
        for (let i = 0; i < 20; i++) {
            const alpha = (i / 20) * 0.5;
            g.fillStyle(0x000000, alpha);
            g.fillRect(0, 0, 800, 600);
            g.fillStyle(0x000000, 0); // Clear center
            g.fillCircle(400, 300, vignetteSize - i * 15);
        }
        g.generateTexture('vignette', 800, 600);

        g.destroy();
    }

    createDataPoints() {
        const g = this.scene.make.graphics({ add: false });

        // Correct data point (green circle)
        g.fillStyle(CONSTANTS.COLORS.DATA_CORRECT, 0.8);
        g.fillCircle(20, 20, 18);
        g.fillStyle(CONSTANTS.COLORS.DATA_CORRECT);
        g.fillCircle(20, 20, 12);
        g.fillStyle(0xFFFFFF, 0.5);
        g.fillCircle(16, 16, 4);
        g.generateTexture('data_correct', 40, 40);
        g.clear();

        // Outlier data point (red triangle/spiky)
        g.fillStyle(CONSTANTS.COLORS.DATA_OUTLIER);
        g.fillTriangle(20, 2, 2, 38, 38, 38);
        // Inner glow
        g.fillStyle(0xFF6666);
        g.fillCircle(20, 25, 8);
        // Eye
        g.fillStyle(0x000000);
        g.fillCircle(20, 25, 4);
        g.generateTexture('data_outlier', 40, 40);

        g.destroy();
    }

    createOverfitter() {
        const g = this.scene.make.graphics({ add: false });

        // Large menacing silhouette
        // Shadow base
        g.fillStyle(0x0a0505, 0.9);
        g.fillEllipse(100, 120, 180, 220);

        // Body form
        g.fillStyle(CONSTANTS.COLORS.OVERFITTER_SHADOW, 0.95);
        g.fillEllipse(100, 115, 160, 200);

        // Darker patches
        g.fillStyle(0x0a0000, 0.6);
        g.fillEllipse(70, 100, 40, 50);
        g.fillEllipse(130, 130, 45, 55);

        // Hollow eye sockets
        g.fillStyle(0x000000);
        g.fillEllipse(70, 90, 35, 45);
        g.fillEllipse(130, 90, 35, 45);

        // Dim red pupils
        g.fillStyle(0x440000);
        g.fillCircle(70, 93, 10);
        g.fillCircle(130, 93, 10);

        // Subtle glow
        g.fillStyle(0xFF0000, 0.2);
        g.fillCircle(70, 93, 18);
        g.fillCircle(130, 93, 18);

        g.generateTexture('overfitter', 200, 240);
        g.destroy();
    }

    createParticles() {
        const g = this.scene.make.graphics({ add: false });

        // Error particle
        g.fillStyle(0xFF0000);
        g.fillCircle(4, 4, 4);
        g.generateTexture('particle_error', 8, 8);
        g.clear();

        // Glow particle
        g.fillStyle(0xFFFFFF, 0.8);
        g.fillCircle(8, 8, 8);
        g.fillStyle(0xFFFFFF, 0.4);
        g.fillCircle(8, 8, 6);
        g.fillStyle(0xFFFFFF);
        g.fillCircle(8, 8, 3);
        g.generateTexture('particle_glow', 16, 16);

        g.destroy();
    }
}
